import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tabs, Input, Spin, Tooltip, Form, Select, Button, Empty, message, notification, Col, Row, Modal, Card, Statistic, Progress, Table } from 'antd';
import type { TabsProps } from 'antd';
import { InfoCircleOutlined, SaveOutlined, CalculatorOutlined, HistoryOutlined, ReloadOutlined } from '@ant-design/icons';
import { isEmpty } from 'underscore';

import { AppDispatch, CombinedStates } from '../../core/types';
import { IndicatorCategoryStats } from '../../core/school/types';
import { getSchoolIndicatorLogs, getSchoolIndicators, updateSchoolIndicators } from '../../core/indicator/indicatorApi';
import { getSingleSchool } from '../../core/school/schoolApi';
import { getProgressColor, formatNumber } from '../../core/utils/helpers';

import './schoolIndicatorsStatistics.scss';

const { Option } = Select;

const actionLabels: Record<string, string> = {
    created: 'Sukurta',
    updated: 'Atnaujinta',
    deleted: 'Ištrinta',
    computed: 'Apskaičiuota',
    compute_error: 'Skaičiavimo klaida',
};

const allowedRolesForCritical = [1, 2, 3, 4];

const SchoolIndicatorsStatistics = () => {
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const [activeTab, setActiveTab] = useState<string>('');
    const [logModalVisible, setLogModalVisible] = useState(false);

    const { singleSchool, isFetching } = useSelector((state: CombinedStates) => state.school);
    const { statistics } = singleSchool || {};
    const { userData } = useSelector((state: CombinedStates) => state.user);

    const handleRefreshStatistics = () => {
        if (singleSchool?.id) {
            dispatch(getSingleSchool({ id: singleSchool.id, refreshStatistics: true }));
        }
    };

    const getMissingColorByPercentage = (filled: number, total: number) => {
        if (total === 0) return '#d9d9d9'; // neutral gray
        const percent = (filled / total) * 100;
        if (percent === 100) return '#52c41a'; // green
        if (percent >= 70) return '#fadb14'; // yellow
        if (percent >= 40) return '#fa8c16'; // orange
        return '#ff4d4f'; // red
    };

    const columns = [
        {
            title: 'Kategorija',
            dataIndex: 'category',
            key: 'category',
        },
        {
            title: 'Progresas',
            key: 'progress',
            render: (_: any, record: IndicatorCategoryStats) => {
                const { percentage, filled, missing, total } = record;
                return (
                    <>
                        <Progress
                            percent={percentage}
                            strokeColor={getProgressColor(percentage)}
                            status="active" />
                        <p style={{ marginTop: 8 }}>
                            Įvesti rodikliai: <strong>{filled}</strong> / Iš viso: <strong>{total}</strong><br />
                            Trūkstami rodikliai: <strong style={{ color: getProgressColor(percentage) }}>{missing}</strong>
                        </p>
                    </>
                );
            },
        },
    ];

    return (
        <div className="school-indicators-wrapper">
            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    onClick={handleRefreshStatistics}
                    loading={isFetching}>
                    Atnaujinti statistiką
                </Button>
            </div>
            <Spin spinning={isFetching}>
                {statistics && !isEmpty(statistics) ? (
                    <>
                        <Row gutter={16} style={{ marginBottom: 24 }}>
                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#f0f5ff', borderColor: '#2f54eb' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Priskirti rodikliai"
                                        value={formatNumber(statistics.total_assigned)} />
                                </Card>
                            </Col>

                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#f6ffed', borderColor: '#52c41a' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Įvesti rodikliai"
                                        value={formatNumber(statistics.total_filled)} />
                                </Card>
                            </Col>

                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#fff2e8', borderColor: getProgressColor(statistics.total_percentage) }}
                                    bordered={false}>
                                    <Statistic
                                        title="Trūkstami rodikliai"
                                        value={formatNumber(statistics.total_missing)}
                                        valueStyle={{
                                            color: getProgressColor(statistics.total_percentage),
                                        }} />
                                </Card>
                            </Col>

                            <Col span={6}>
                                <Card
                                    style={{ backgroundColor: '#fffbe6', borderColor: '#faad14' }}
                                    bordered={false}>
                                    <Statistic
                                        title="Kritiniai (įvesta / viso)"
                                        value={`${formatNumber(statistics.critical_filled)} / ${formatNumber(statistics.critical_total)}`}
                                        suffix={`(${statistics.critical_percentage}%)`}
                                        valueStyle={{ color: getProgressColor(statistics.critical_percentage) }} />
                                </Card>
                            </Col>
                        </Row>

                        {/* Overall progress bar */}
                        <Row style={{ marginBottom: 32 }}>
                            <Col span={24}>
                                <h3>Rodiklių užpildymo progresas</h3>
                                <Progress
                                    percent={statistics.total_percentage}
                                    strokeColor={getProgressColor(statistics.total_percentage)}
                                    status="active" />
                                <p style={{ marginTop: 8 }}>
                                    Įvesti rodikliai: <strong>{formatNumber(statistics.total_filled)}</strong> / Iš viso: <strong>{formatNumber(statistics.total_assigned)}</strong>
                                </p>
                            </Col>
                        </Row>

                        {/* Category table */}
                        <Row>
                            <Col span={24}>
                                <h3>Statistika pagal kategorijas</h3>
                                <Table
                                    columns={columns}
                                    dataSource={statistics.categories}
                                    rowKey="category"
                                    pagination={false} />
                            </Col>
                        </Row>
                        <Row style={{ marginTop: 32 }}>
                            <Col span={24}>
                                <h4>Progreso spalvų reikšmės</h4>
                                <ul>
                                    <li><span style={{ color: '#52c41a' }}>Žalia</span> – 100% įvesti</li>
                                    <li><span style={{ color: '#fadb14' }}>Geltona</span> – 70–99%</li>
                                    <li><span style={{ color: '#fa8c16' }}>Oranžinė</span> – 40–69%</li>
                                    <li><span style={{ color: '#ff4d4f' }}>Raudona</span> – mažiau nei 40%</li>
                                </ul>
                            </Col>
                        </Row>
                    </>
                ) : (
                    <Empty description="Nėra duomenų" />
                )}
            </Spin>

        </div>
    );
};

export default SchoolIndicatorsStatistics;
